/**
 * Throughput specific parsing logic
 */

import { readFile } from '../shared/fileReader';
import { normalizeThroughputDates } from '../shared/dateNormalizer';
import { addWeekField } from '../shared/weekCalculator';
import { formatDateSafe } from '../../dateUtils';
import { 
  validateHeaders as validateThroughputHeaders, 
  generateHeaderError as generateThroughputHeaderError, 
  generateValidationError as generateThroughputValidationError, 
  validateData as validateThroughputData 
} from '../../validations/throughput/validator';

/**
 * Processes a single throughput item
 * @param {Object} item - Raw item data
 * @returns {Object} Processed item for throughput analysis
 */
function processThroughputItem(item) {
  const { closedDate } = normalizeThroughputDates(item);
  
  const processedItem = {
    ...item,
    closedDate,
    'Closed Date': formatDateSafe(closedDate)
  };
  
  return addWeekField(processedItem);
}

/**
 * Parses a file for throughput analysis
 * @param {File} file - The file to parse
 * @returns {Array} Processed data array for throughput calculations
 */
export async function parseThroughputFile(file) {
  // Read file data
  const { headers, rawData } = await readFile(file);
  
  // Validate headers
  const missingHeaders = validateThroughputHeaders(headers);
  if (missingHeaders.length > 0) {
    throw new Error(generateThroughputHeaderError(missingHeaders, headers));
  }

  // Validate data
  const { validationErrors, validData } = validateThroughputData(rawData);
  if (validationErrors.length > 0) {
    throw new Error(generateThroughputValidationError(validationErrors));
  }

  // Process valid data
  return validData.map(processThroughputItem);
}
